// prepareListPageMeta.js
import { BRANDING_NAME } from '../RemoteConfigKeys.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { getI18nInstance } from '../resource/i18n.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import { generateItemListJsonLd } from '../resource/generateItemListJsonLd.js';
import { generateWebSiteJsonLd } from '../resource/generateWebSiteJsonLd.js';

/**
 * Prepare meta data for list pages (feed, video, shorts, etc.)
 * @param {Object} params
 * @param {Object} params.state - Redux state
 * @param {string} params.titleKey - Translation key for title (e.g., 'seo-home-title')
 * @param {string} params.descriptionKey - Translation key for description (e.g., 'seo-home-description')
 * @param {string} params.keywordsKey - Translation key for keywords (optional)
 * @param {string} params.hostname - Request hostname
 * @param {string} params.canonical - Canonical URL
 * @param {Array} params.alternates - Alternate language links
 * @param {boolean} params.includeJsonLd - Whether to include JSON-LD
 * @param {string} params.jsonLdType - Type of JSON-LD ('ItemList' or 'WebSite')
 * @param {Array<Object>} params.items - Array of items for ItemList
 * @param {number} params.numberOfItems - Total number of items
 * @param {Array<string>} params.robots - Array of robots directives (e.g., ['noindex', 'nofollow'])
 * @returns {Promise<Array>} Meta tags array for Helmet
 */
export const prepareListPageMeta = async ({
  state,
  titleKey,
  descriptionKey,
  keywordsKey,
  hostname = 'swag.live',
  canonical = null,
  alternates = null,
  includeJsonLd = false,
  jsonLdType = 'ItemList',
  items = [],
  numberOfItems,
  robots = [],
}) => {
  // Get i18n instance
  const { i18n } = getI18nInstance() || {};

  // Get branding name
  const brandingName = getRemoteConfigData(state, BRANDING_NAME) || 'SWAG';

  // Get translated content
  const title = i18n?.t?.(titleKey, { ns: TranslationNamespace.SEO })
    ? `${i18n.t(titleKey, { ns: TranslationNamespace.SEO })} | ${brandingName}`
    : brandingName;
  const description =
    i18n?.t?.(descriptionKey, { ns: TranslationNamespace.SEO }) || '';
  const keywordsString = keywordsKey
    ? i18n?.t?.(keywordsKey, { ns: TranslationNamespace.SEO }) || ''
    : '';
  const keywords = keywordsString
    ? keywordsString.split(',').map(k => k.trim())
    : [];

  // Prepare JSON-LD if requested
  let jsonLd = null;
  if (includeJsonLd && canonical) {
    const fullUrl = new URL(`https://${hostname}`);
    try {
      if (jsonLdType === 'WebSite') {
        jsonLd = await generateWebSiteJsonLd({
          state,
          url: canonical,
          name: title,
          description,
          fullUrl,
        });
      } else {
        jsonLd = await generateItemListJsonLd({
          url: canonical,
          name: title,
          description,
          items,
          fullUrl,
          numberOfItems,
        });
      }
    } catch (error) {
      jsonLd = null;
    }
  }

  return getMetaTagsForHelmet({
    title,
    description,
    keywords,
    hostname,
    ogType: 'website',
    url: canonical,
    siteName: brandingName,
    twitterCard: 'summary_large_image',
    alternates: {
      canonical,
      alternates,
    },
    robots,
    jsonLd,
  });
};

export default prepareListPageMeta;
